import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataFieldType, Trial } from "@/api/dtos";
import useSourceDataItemGroupGrid from "@/hooks/form/source-data/useSourceDataItemGroupGrid";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled, SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme, useTheme } from "@mui/material"
import { FC } from "react";
import { SourceDataItemMapping } from "../values/Index";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
            display: 'flex',
            alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

interface ISourceDataItemGroupGridProps {
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant?: Participant;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    preview?: boolean;
    onClose?: () => void;
}

const SourceDataItemGroupGrid: FC<ISourceDataItemGroupGridProps> = ({
    metadata,
    trialConfiguration,
    configuration,
    sourceData,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    preview,
    onClose
}) => {

    const theme = useTheme();

    const grid = useSourceDataItemGroupGrid(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        preview
    )

    if (!grid) {
        return null;
    }

    return (
        <StyledDialog
            //onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={true}
            maxWidth="xl"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                {grid.label}
            </DialogTitle>
            <DialogContent dividers sx={{p:0}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {grid.columns?.map((column, index) => (
                                <TableCell
                                    key={index}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {grid.rows?.map((row) => (
                            <TableRow
                                key={row.repeat}
                            >
                                {row.cells?.map((cell) => {
                                    const ValueComponent = SourceDataItemMapping[cell?.itemMetadata?.fieldType ?? SourceDataFieldType.Text];

                                    const sx: SxProps<Theme> = {
                                        minWidth: theme.spacing(12.5)
                                    }

                                    switch (cell?.itemMetadata?.fieldType ?? SourceDataFieldType.Text) {
                                        case SourceDataFieldType.MultilineText:
                                            sx.minWidth = theme.spacing(37.5);
                                            break;
                                        case SourceDataFieldType.Text:
                                            sx.whiteSpace = 'nowrap';
                                            break;
                                        case SourceDataFieldType.Select:
                                            sx.whiteSpace = 'nowrap';
                                            break;
                                    }

                                    return (
                                        <TableCell
                                            key={cell?.itemName}
                                            sx={sx}
                                        >
                                            <ValueComponent 
                                                itemData={cell}
                                                hideBorderStyles
                                                hideStyles
                                            />
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <FormButtons>
                    { 
                        onClose && (
                            <Button
                                variant="contained"
                                color="inherit"
                                onClick={onClose}
                            >
                                Close
                            </Button>
                        )
                    }
                </FormButtons>
            </DialogActions>
        </StyledDialog>
    );
}

export default SourceDataItemGroupGrid;